import React, { useState } from 'react';
import axios from 'axios';
import { Shield, Pencil, Trash2, Save, AlertCircle } from 'lucide-react';
import { Modal, SearchBar, matchesSearch, formatDate, getRoleBadgeClass, getRoleLabel, getRoleLabelAdmin, getStatusBadgeClass, getStatusLabel, ProjectInput, confirmDialog, alertDialog } from './shared';

export default function UsersTab({ user, authHeaders, API_URL, isAdmin, allUsers, setAllUsers, searchTerm, setSearchTerm, projects }) {
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ first_name: '', last_name: '', email: '', username: '', role: '', user_status: '', bio: '', sponsor_username: '', birthday_day: '', birthday_month: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const openEditUser = (u) => {
    setEditingUser(u);
    const bd = u.birthday || '';
    const parts = bd.split('-');
    setEditUserForm({ first_name: u.first_name, last_name: u.last_name, email: u.email, username: u.username || '', role: u.role, user_status: u.status, bio: u.bio || '', sponsor_username: u.sponsor_username || '', birthday_month: parts[0] ? String(parseInt(parts[0])) : '', birthday_day: parts[1] ? String(parseInt(parts[1])) : '' });
    setShowEditUser(true);
    setFormError('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setFormLoading(true); setFormError('');
    try {
      const birthday = editUserForm.birthday_month && editUserForm.birthday_day
        ? `${editUserForm.birthday_month.padStart(2, '0')}-${editUserForm.birthday_day.padStart(2, '0')}`
        : '';
      const { birthday_day, birthday_month, ...rest } = editUserForm;
      await axios.put(`${API_URL}/api/community/admin/users/${editingUser.id}`, { ...rest, birthday }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setShowEditUser(false);
      const resp = await axios.get(`${API_URL}/api/community/admin/users`, { headers: authHeaders });
      setAllUsers(resp.data.users || []);
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    if (!(await confirmDialog('Benutzer wirklich löschen? Dies kann nicht rückgängig gemacht werden.', { title: 'Benutzer löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/admin/users/${userId}`, { headers: authHeaders });
      setAllUsers(allUsers.filter(u => u.id !== userId));
      if (showEditUser && editingUser?.id === userId) setShowEditUser(false);
    } catch (e) { alertDialog(e.response?.data?.detail || 'Fehler beim Löschen', { title: 'Fehler', danger: true }); }
  };

  return (
    <div className="tab-content" data-testid="users-content">
      <div className="section-card"><h3><Shield size={18} /> Alle Benutzer ({allUsers.length})</h3>
        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Benutzer suchen..." />
        <div className="user-table-wrap"><table className="user-table"><thead><tr><th>Name</th><th>E-Mail</th><th>Benutzername</th><th>Rolle</th><th>Status</th><th>Datum</th>{isAdmin && <th>Aktionen</th>}</tr></thead><tbody>
          {allUsers.filter(u => matchesSearch(u, searchTerm, ['first_name', 'last_name', 'email', 'username', 'role', 'status'])).map(u => <tr key={u.id}><td>{u.first_name} {u.last_name}</td><td>{u.email}</td><td>{u.username}</td><td><span className={`role-badge ${getRoleBadgeClass(u.role)}`}>{getRoleLabelAdmin(u.role)}</span></td><td><span className={`status-badge ${getStatusBadgeClass(u.status)}`}>{getStatusLabel(u.status)}</span></td><td>{formatDate(u.created_at)}</td>
            {isAdmin && <td className="actions-cell">
              <button className="btn-icon" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}><Pencil size={13} /></button>
              {u.id !== user?.id && <button className="btn-icon danger" onClick={() => handleDeleteUser(u.id)} data-testid={`del-user-${u.id}`}><Trash2 size={13} /></button>}
            </td>}
          </tr>)}
        </tbody></table></div>
      </div>

      {/* User Edit Modal */}
      <Modal show={showEditUser} onClose={() => setShowEditUser(false)} title={editingUser ? `${editingUser.first_name} ${editingUser.last_name} bearbeiten` : 'Benutzer bearbeiten'} preventBackdropClose>
        <form onSubmit={handleUpdateUser} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-row">
            <div className="form-group"><label>Vorname</label><input value={editUserForm.first_name} onChange={e => setEditUserForm({...editUserForm, first_name: e.target.value})} data-testid="edit-user-firstname" /></div>
            <div className="form-group"><label>Nachname</label><input value={editUserForm.last_name} onChange={e => setEditUserForm({...editUserForm, last_name: e.target.value})} data-testid="edit-user-lastname" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>E-Mail</label><input type="email" value={editUserForm.email} onChange={e => setEditUserForm({...editUserForm, email: e.target.value})} data-testid="edit-user-email" /></div>
            <div className="form-group"><label>Benutzername</label><input value={editUserForm.username} onChange={e => setEditUserForm({...editUserForm, username: e.target.value})} data-testid="edit-user-username" /></div>
            <div className="form-group"><label>Förderer (Benutzername)</label><input value={editUserForm.sponsor_username} onChange={e => setEditUserForm({...editUserForm, sponsor_username: e.target.value})} placeholder="Benutzername des Förderers" data-testid="edit-user-sponsor" /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Rolle</label>
              <select value={editUserForm.role} onChange={e => setEditUserForm({...editUserForm, role: e.target.value})} className="modal-select" data-testid="edit-user-role">
                <option value="user">Freigeist</option>
                <option value="sponsor">Förderer</option>
                <option value="coach">Botschafter</option>
                <option value="admin">Admin-Botschafter</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={editUserForm.user_status} onChange={e => setEditUserForm({...editUserForm, user_status: e.target.value})} className="modal-select" data-testid="edit-user-status">
                <option value="active">Aktiv</option>
                <option value="pending">Ausstehend (Förderer-Freigabe)</option>
                <option value="email_pending">E-Mail-Bestätigung ausstehend</option>
                <option value="banned">Gesperrt</option>
                <option value="rejected">Abgelehnt</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label>Bio</label><textarea value={editUserForm.bio} onChange={e => setEditUserForm({...editUserForm, bio: e.target.value})} rows={3} data-testid="edit-user-bio" /></div>
          <div className="form-group">
            <label>Geburtstag (Tag &amp; Monat)</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select value={editUserForm.birthday_day || ''} onChange={e => setEditUserForm({...editUserForm, birthday_day: e.target.value})} className="modal-select" data-testid="edit-user-birthday-day">
                <option value="">Tag</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d)}>{d}</option>
                ))}
              </select>
              <select value={editUserForm.birthday_month || ''} onChange={e => setEditUserForm({...editUserForm, birthday_month: e.target.value})} className="modal-select" data-testid="edit-user-birthday-month">
                <option value="">Monat</option>
                {['Januar','Februar','Maerz','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                  <option key={i+1} value={String(i+1)}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          {projects && projects.length > 0 && (
            <div className="form-group">
              <label>Projekt User-IDs</label>
              <div className="projects-grid">
                {projects.map(p => (
                  <ProjectInput key={p.id} project={p} initialValue={(editingUser?.project_ids || {})[p.id] || ''} apiUrl={API_URL} authHeaders={authHeaders} targetUserId={editingUser?.id} />
                ))}
              </div>
            </div>
          )}
          <div className="modal-actions">
            <button type="submit" className="btn-login" disabled={formLoading} data-testid="edit-user-submit"><Save size={16} /> {formLoading ? 'Speichern...' : 'Speichern'}</button>
            {editingUser && editingUser.id !== user?.id && (
              <button type="button" className="btn-delete" onClick={() => handleDeleteUser(editingUser.id)} data-testid="edit-user-delete"><Trash2 size={16} /> Benutzer löschen</button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}
